import { useState, useRef, useCallback, useEffect } from "react";

const URL_REGEX = /https?:\/\/[^\s<>"{}|\\^`\[\]]+/gi;

export interface ComposeLinkPreview {
  url: string;
  title: string | null;
  description: string | null;
  imageUrl: string | null;
  siteName: string | null;
  favicon: string | null;
}

/**
 * Detects URLs in text and fetches OG metadata for the first one.
 * Returns the preview data and a dismiss function.
 */
export function useLinkPreview() {
  const [preview, setPreview] = useState<ComposeLinkPreview | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const lastFetchedUrl = useRef<string | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const dismiss = useCallback(() => {
    setPreview(null);
    setDismissed(true);
  }, []);

  const reset = useCallback(() => {
    setPreview(null);
    setDismissed(false);
    setIsLoading(false);
    lastFetchedUrl.current = null;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
  }, []);

  const detectAndFetch = useCallback((text: string) => {
    if (dismissed) return;

    const urls = text.match(URL_REGEX);
    const firstUrl = urls?.[0] || null;

    // No URL found in the text. Keep any already-loaded preview *pinned* — the user
    // may have intentionally deleted the URL but wants to keep the card (the bound
    // URL is still sent with the post). Only cancel an in-flight fetch whose URL is
    // no longer present so a half-loaded preview doesn't pop in after deletion.
    if (!firstUrl) {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      setIsLoading(false);
      return;
    }

    // Same URL already fetched
    if (firstUrl === lastFetchedUrl.current) return;

    // Debounce the fetch
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(async () => {
      lastFetchedUrl.current = firstUrl;
      setIsLoading(true);
      try {
        const res = await fetch(`/api/link-preview?url=${encodeURIComponent(firstUrl)}`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error("Failed");
        const data = await res.json();
        // Only apply if the URL is still the one we're expecting
        if (lastFetchedUrl.current === firstUrl) {
          setPreview(data);
        }
      } catch {
        if (lastFetchedUrl.current === firstUrl) {
          setPreview(null);
        }
      } finally {
        if (lastFetchedUrl.current === firstUrl) {
          setIsLoading(false);
        }
      }
    }, 500);
  }, [dismissed]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  // The URL bound to the currently-shown preview. Send this with the post as
  // `embedUrl` so the link stays attached even if the user removed it from the text.
  const boundUrl = preview?.url ?? null;

  return { preview, isLoading, dismiss, reset, detectAndFetch, boundUrl };
}
